// Shared data + helpers for the EstimateWizard. Extracted from
// EstimateWizard.jsx to keep that component manageable.
import {
  Home as HomeIcon, Building2, Truck, Sofa, Zap, Trash2, Boxes, Key,
  Package, BedDouble, Refrigerator, WashingMachine, Tv, Armchair,
  PackageOpen, Trees, Hammer, FileText, Monitor, ShoppingBag,
  Baby, Archive, Frame, BookOpen, Lamp, Piano, Utensils, CookingPot, Microwave, Fan, Shirt, Laptop, Heater, Speaker, Gamepad2, Container, Bike, Dumbbell, Flower2,
} from 'lucide-react';

export const SERVICES = [
  { id: 'House & Flat Removal', icon: HomeIcon, hint: 'Studios to family homes' },
  { id: 'Office / Commercial Move', icon: Building2, hint: 'Shops & offices' },
  { id: 'Man & Van', icon: Truck, hint: 'Small moves & deliveries' },
  { id: 'Furniture Delivery', icon: Sofa, hint: 'Single or multi-item' },
  { id: 'Same-Day Collection', icon: Zap, hint: 'Urgent moves today' },
  { id: 'Rubbish Clearance', icon: Trash2, hint: 'Waste & bulky items' },
  { id: 'House / Garage Clearance', icon: Boxes, hint: 'Full property clearance' },
  { id: 'End-of-Tenancy', icon: Key, hint: 'Landlords & agents' },
];

// Service classification helpers
export const isClearanceSvc = (s) => /clearance|rubbish|waste|tenancy/i.test(s || '');
export const isOfficeSvc = (s) => /office|commercial/i.test(s || '');
export const isDeliverySvc = (s) => /delivery|same-day|single|man & van/i.test(s || '');

export const SIZES_MOVE = ['Studio / Single Item', '1-bed flat', '2-bed flat', '2-bed house', '3-bed house', '4-bed house', '5-bed house', '6-bed+ house (2 vans)', 'Office / Commercial'];
export const SIZES_CLEARANCE = ['Just a few items', '1-bed flat worth', '2-bed flat worth', '3-bed house worth', '4-bed house worth', '5-bed+ worth', 'Garage only', 'Garden only', 'Shed / loft only', 'Office / Commercial'];
export const ACCESS = ['Ground floor', 'With lift', '1 flight stairs', '2 flights stairs', '3+ flights stairs'];

// Item counters for MOVES
export const MOVE_ITEMS = [
  // Bedroom
  { id: 'single_beds', group: 'Bedroom', label: 'Single beds', hint: 'frame + mattress', icon: BedDouble },
  { id: 'double_beds', group: 'Bedroom', label: 'Double / King beds', hint: 'frame + mattress', icon: BedDouble },
  { id: 'bunk_beds', group: 'Bedroom', label: 'Bunk beds', hint: 'usually dismantled', icon: BedDouble },
  { id: 'cots', group: 'Bedroom', label: 'Cots / toddler beds', hint: 'small frame', icon: Baby },
  { id: 'mattresses', group: 'Bedroom', label: 'Spare mattresses', hint: 'mattress only', icon: BedDouble },
  { id: 'wardrobes', group: 'Bedroom', label: 'Wardrobes', hint: 'per unit (heavy)', icon: PackageOpen },
  { id: 'chest_drawers', group: 'Bedroom', label: 'Chests of drawers', hint: 'any size', icon: Archive },
  { id: 'dressing_tables', group: 'Bedroom', label: 'Dressing tables', hint: 'with or without mirror', icon: Frame },
  { id: 'bedside_tables', group: 'Bedroom', label: 'Bedside tables', hint: 'small units', icon: Archive },
  { id: 'mirrors', group: 'Bedroom', label: 'Mirrors / large pictures', hint: 'need wrapping', icon: Frame },
  // Living room
  { id: 'sofa_2', group: 'Living room', label: '2-seater sofa', hint: 'small/medium', icon: Sofa },
  { id: 'sofa_3', group: 'Living room', label: '3-seater sofa', hint: 'standard full-size', icon: Sofa },
  { id: 'sofa_corner', group: 'Living room', label: 'Corner / L-shape sofa', hint: 'may need dismantle', icon: Sofa },
  { id: 'sofa_bed', group: 'Living room', label: 'Sofa-bed', hint: 'heavier than a sofa', icon: Sofa },
  { id: 'suite_3pc', group: 'Living room', label: '3-piece suite', hint: '3-seater + 2 armchairs', icon: Sofa },
  { id: 'armchairs', group: 'Living room', label: 'Armchairs / recliners', hint: 'single-seat', icon: Armchair },
  { id: 'coffee_tables', group: 'Living room', label: 'Coffee / side tables', hint: 'small tables', icon: Package },
  { id: 'tv_units', group: 'Living room', label: 'TV stands / units', hint: 'media units', icon: Tv },
  { id: 'tvs', group: 'Living room', label: 'TVs', hint: 'any size', icon: Tv },
  { id: 'bookcases', group: 'Living room', label: 'Bookcases / shelving', hint: 'tall units', icon: BookOpen },
  { id: 'cabinets', group: 'Living room', label: 'Cupboards / sideboards', hint: 'display, storage', icon: Archive },
  { id: 'lamps', group: 'Living room', label: 'Lamps', hint: 'floor or table', icon: Lamp },
  { id: 'rugs', group: 'Living room', label: 'Rugs', hint: 'rolled', icon: Package },
  { id: 'piano', group: 'Living room', label: 'Piano', hint: 'upright (heavy)', icon: Piano },
  // Kitchen & dining
  { id: 'dining_tables', group: 'Kitchen & dining', label: 'Dining tables', hint: 'table only', icon: Utensils },
  { id: 'dining_chairs', group: 'Kitchen & dining', label: 'Dining chairs', hint: 'per chair', icon: Armchair },
  { id: 'fridges', group: 'Kitchen & dining', label: 'Fridges / freezers', hint: 'standard size', icon: Refrigerator },
  { id: 'american_fridge', group: 'Kitchen & dining', label: 'American fridge-freezer', hint: 'very heavy', icon: Refrigerator },
  { id: 'washing_machines', group: 'Kitchen & dining', label: 'Washing machines', hint: '~70–90kg', icon: WashingMachine },
  { id: 'tumble_dryers', group: 'Kitchen & dining', label: 'Tumble dryers', hint: 'freestanding', icon: WashingMachine },
  { id: 'dishwashers', group: 'Kitchen & dining', label: 'Dishwashers', hint: 'freestanding', icon: WashingMachine },
  { id: 'cookers', group: 'Kitchen & dining', label: 'Cookers / ovens', hint: 'freestanding', icon: CookingPot },
  { id: 'microwaves', group: 'Kitchen & dining', label: 'Microwaves', hint: 'countertop', icon: Microwave },
  { id: 'small_appliances', group: 'Kitchen & dining', label: 'Small appliances', hint: 'kettle, toaster, air fryer…', icon: CookingPot },
  { id: 'kitchen_cupboards', group: 'Kitchen & dining', label: 'Freestanding cupboards', hint: 'larder, dresser', icon: Archive },
  // Around the home
  { id: 'hoovers', group: 'Around the home', label: 'Hoovers / vacuums', hint: 'upright or cordless', icon: Fan },
  { id: 'ironing', group: 'Around the home', label: 'Ironing boards / airers', hint: 'folding', icon: Shirt },
  { id: 'home_desks', group: 'Around the home', label: 'Desks', hint: 'home office', icon: Laptop },
  { id: 'home_office_chairs', group: 'Around the home', label: 'Office chairs', hint: 'swivel', icon: Armchair },
  { id: 'computers_home', group: 'Around the home', label: 'Computers / printers', hint: 'monitor + tower', icon: Monitor },
  { id: 'heaters_fans', group: 'Around the home', label: 'Heaters / fans', hint: 'portable', icon: Heater },
  { id: 'speakers', group: 'Around the home', label: 'Speakers / hi-fi', hint: 'sound system', icon: Speaker },
  { id: 'kids_toys', group: 'Around the home', label: 'Toys / playsets', hint: 'per large item or crate', icon: Gamepad2 },
  { id: 'pushchairs', group: 'Around the home', label: 'Pushchairs / high chairs', hint: 'baby gear', icon: Baby },
  { id: 'suitcases', group: 'Around the home', label: 'Suitcases / bags', hint: 'packed', icon: ShoppingBag },
  { id: 'storage_tubs', group: 'Around the home', label: 'Plastic storage tubs', hint: 'large lidded', icon: Container },
  // Garden & garage
  { id: 'bikes', group: 'Garden & garage', label: 'Bikes', hint: 'adult or kids', icon: Bike },
  { id: 'gym_equipment', group: 'Garden & garage', label: 'Gym equipment', hint: 'bench, weights, bike', icon: Dumbbell },
  { id: 'treadmills', group: 'Garden & garage', label: 'Treadmills / cross-trainers', hint: 'heavy', icon: Dumbbell },
  { id: 'garden_furniture', group: 'Garden & garage', label: 'Garden furniture', hint: 'table / chair set', icon: Flower2 },
  { id: 'bbq', group: 'Garden & garage', label: 'BBQs', hint: 'gas or charcoal', icon: Flower2 },
  { id: 'lawnmowers', group: 'Garden & garage', label: 'Lawnmowers / garden tools', hint: 'per item', icon: Trees },
  { id: 'plants', group: 'Garden & garage', label: 'Large plant pots', hint: 'per pot', icon: Flower2 },
];

// Item counters for OFFICE moves
export const OFFICE_ITEMS = [
  { id: 'desks', label: 'Desks', icon: Package },
  { id: 'office_chairs', label: 'Office chairs', icon: Armchair },
  { id: 'filing_cabinets', label: 'Filing cabinets', icon: FileText },
  { id: 'computers', label: 'Computers / monitors', icon: Monitor },
  { id: 'printers', label: 'Printers / copiers', icon: Package },
  { id: 'bookshelves', label: 'Bookshelves', icon: PackageOpen },
  { id: 'meeting_tables', label: 'Meeting tables', icon: Package },
  { id: 'server_racks', label: 'Server racks / IT equipment', icon: Monitor },
];

// Item counters for CLEARANCE
export const CLEARANCE_ITEMS = [
  // Bags & light waste
  { id: 'bin_bags', label: 'Bin bags (black bags)', hint: '~5–10kg each', icon: ShoppingBag },
  { id: 'c_loose', label: 'Loose / open rubbish', hint: 'unbagged, piled up', icon: Trash2 },
  { id: 'cardboard', label: 'Cardboard / paper', hint: 'flat-packed or loose', icon: FileText },
  { id: 'c_polystyrene', label: 'Polystyrene / packaging foam', hint: 'bulky but light', icon: Package },

  // Sofas & suites
  { id: 'c_sofa_2', label: '2-seater sofa', hint: '~£60 collection', icon: Sofa },
  { id: 'c_sofa_3', label: '3-seater sofa', hint: '~£75 collection', icon: Sofa },
  { id: 'c_sofa_corner', label: 'Corner / L-shape sofa', hint: 'counts as ~1.5 sofas', icon: Sofa },
  { id: 'c_sofa_bed', label: 'Sofa-bed', hint: 'heavier than a normal sofa', icon: Sofa },
  { id: 'c_suite_3pc', label: '3-piece suite', hint: '3-seater + 2 armchairs (~£120)', icon: Sofa },
  { id: 'c_armchairs', label: 'Armchairs / recliners', hint: 'single-seat', icon: Armchair },

  // Beds & bedroom
  { id: 'c_mattresses', label: 'Mattresses', hint: 'single / double / king', icon: BedDouble },
  { id: 'c_divans', label: 'Divan bases', hint: 'often heavier than frame', icon: BedDouble },
  { id: 'c_beds', label: 'Bed frames', hint: 'wooden or metal', icon: BedDouble },
  { id: 'c_wardrobes', label: 'Wardrobes', hint: 'per unit (heavy)', icon: PackageOpen },
  { id: 'c_drawers', label: 'Chests of drawers / dressers', hint: 'per unit', icon: PackageOpen },

  // Kitchen & appliances
  { id: 'c_fridges', label: 'Fridges / small freezers', hint: '+£25 regulated disposal', icon: Refrigerator },
  { id: 'c_fridge_american', label: 'American fridge-freezer', hint: '+£60 (very heavy)', icon: Refrigerator },
  { id: 'c_appliances', label: 'Washing machines / dryers / dishwashers', hint: '~70–90kg each', icon: WashingMachine },
  { id: 'c_cooker', label: 'Cookers / ovens / range', hint: 'disconnect first', icon: Package },
  { id: 'c_microwave', label: 'Microwaves / small kitchen gear', hint: 'any size', icon: Package },

  // Electronics
  { id: 'c_tvs', label: 'TVs / monitors / electronics', hint: 'any size', icon: Tv },

  // Dining / living
  { id: 'c_dining_table', label: 'Dining tables + chairs', hint: 'per set', icon: Package },
  { id: 'c_bookshelf', label: 'Bookshelves / display units', hint: 'per unit', icon: PackageOpen },

  // Wood tiers
  { id: 'wood_light', label: 'Wood offcuts / sticks', hint: 'small bagfuls, ~5–10kg each', icon: Hammer },
  { id: 'wood_heavy', label: 'Heavy timber / planks / joists', hint: '~15–30kg each piece', icon: Hammer },
  { id: 'wood_pallets', label: 'Wooden pallets', hint: 'per pallet', icon: Hammer },
  { id: 'wood_furniture', label: 'Broken wood furniture', hint: 'per piece', icon: Hammer },

  // Rubble & builder
  { id: 'rubble_bags', label: 'Rubble bags', hint: '~10–15kg each (bricks, tiles, plaster)', icon: Hammer },
  { id: 'rubble_loose', label: 'Loose rubble pile', hint: 'wheelbarrow-size: small / medium / large', icon: Hammer },
  { id: 'c_plasterboard', label: 'Plasterboard sheets', hint: 'per sheet, ~10–20kg', icon: Hammer },
  { id: 'c_tiles', label: 'Tiles / broken ceramics', hint: 'per bag or box', icon: Hammer },
  { id: 'c_paint', label: 'Paint tins / solvents', hint: 'hazardous — mention in notes', icon: Hammer },

  // Metal tiers
  { id: 'metal_light', label: 'Scrap metal (light)', hint: 'pipes, brackets, bags of odds', icon: Hammer },
  { id: 'metal_heavy', label: 'Heavy metal (radiator / boiler / bar)', hint: '~50–100kg each', icon: Hammer },
  { id: 'metal_appliances', label: 'Scrap appliances / cookers', hint: 'per piece', icon: Hammer },
  { id: 'metal_bikes', label: 'Bicycles / scooters', hint: 'per bike', icon: Hammer },

  // Garden
  { id: 'c_garden', label: 'Garden waste (branches, hedge, grass)', hint: 'per bag', icon: Trees },
  { id: 'soil_bags', label: 'Soil / turf bags', hint: '~20–25kg each', icon: Trees },
  { id: 'c_shed', label: 'Shed / dismantled outbuilding', hint: 'includes break-down', icon: Trees },
  { id: 'c_garden_furniture', label: 'Garden furniture / BBQ / parasol', hint: 'per set', icon: Trees },

  // Flooring & finishes
  { id: 'c_carpet', label: 'Carpet / underlay', hint: 'per room', icon: Package },
  { id: 'c_laminate', label: 'Laminate / vinyl flooring', hint: 'per room', icon: Package },

  // Office / commercial
  { id: 'c_office_desks', label: 'Office desks', hint: 'per unit', icon: Building2 },
  { id: 'c_office_chairs', label: 'Office chairs', hint: 'per chair', icon: Building2 },
  { id: 'c_filing', label: 'Filing cabinets', hint: 'per unit (heavy if full)', icon: Building2 },
  { id: 'c_retail', label: 'Shop / retail fittings', hint: 'rails, shelving, signage', icon: Building2 },

  // Specialist / misc
  { id: 'c_piano', label: 'Piano', hint: '+£120 (specialist equipment)', icon: Hammer },
  { id: 'c_pool_table', label: 'Pool table', hint: '+£180', icon: Hammer },
  { id: 'c_hot_tub', label: 'Hot tub', hint: '+£220 (drain first)', icon: Hammer },
  { id: 'c_safe', label: 'Safe', hint: 'tell us the weight', icon: Hammer },
  { id: 'c_flytip', label: 'Fly-tipped / mixed dump', hint: 'describe in notes', icon: Trash2 },
  { id: 'c_general', label: 'General household junk', hint: 'mixed small items', icon: Trash2 },
];

export const WASTE_LOCATIONS = ['Inside the property', 'Outside / kerbside', 'Both inside and outside'];

export const BULKY = ['Piano', 'Safe', 'American fridge', 'Pool table', 'Treadmill', 'Mattress (king)'];
export const BULKY_CLEARANCE = ['Piano', 'Safe', 'American fridge', 'Pool table', 'Hot tub', 'Shed', 'Hazardous waste (paint/chemicals)'];

// Van & crew packages shown to the customer. We intentionally do NOT show
// hourly prices here — the AI gives one firm price at the end based on the
// full job. The AI backend may still recommend a bigger or smaller van if
// the job clearly needs it.
export const VAN_CREW_OPTIONS = [
  { id: 'ai_recommend', title: 'Not sure — let us pick', subtitle: 'We’ll recommend the right van & crew for your job', badge: 'Recommended' },
  { id: '1man_medium', title: '1 Man + Medium Van', subtitle: 'Small deliveries & 1-item moves' },
  { id: '2man_medium', title: '2 Man + Medium Van', subtitle: 'Studios & 1-bed flats' },
  { id: '2man_xlwb', title: '2 Man + XLWB Van', subtitle: '2-bed flats & bulkier loads' },
  { id: '2man_luton', title: '2 Man + Luton Van', subtitle: '2–3 bed homes' },
  { id: '3man_luton', title: '3 Man + Luton Van', subtitle: '3+ bed homes & big clearances' },
  { id: '3man_xlwb_x2', title: '2× (3 Man + XLWB)', subtitle: 'Two vans, two crews — 5-bed homes in one run' },
  { id: '7_5t_lorry', title: '7.5 Tonne Lorry + 3 Man', subtitle: 'Our biggest truck — 5-bed+ in a single trip', badge: 'Best for big moves' },
];
export const VAN_CREW_LABEL = Object.fromEntries(VAN_CREW_OPTIONS.map((o) => [o.id, o.title]));

export const STEP_COUNT = 6;

export const INITIAL_FORM = {
  service: '',
  property_size: '',
  boxes: 0,
  boxes_small: 0,
  boxes_medium: 0,
  boxes_large: 0,
  items: {},
  heavy_items: [],
  waste_location: '',
  pickup_access: 'Ground floor',
  dropoff_access: 'Ground floor',
  van_crew: 'ai_recommend',
  from_postcode: '', from_house_number: '', from_address: '', to_house_number: '', to_address: '',
  to_postcode: '',
  distance_miles: '',
  packing: false,
  dismantle: false,
  date: undefined,
  time: '',
  slot_checked: false,
  notes: '',
  name: '',
  phone: '',
  email: '',
};

export const VOLUME_UNITS = {
  // Boxes
  boxes_small: 0.4,
  boxes_medium: 1,
  boxes_large: 2,
  // Move items
  single_beds: 7,
  double_beds: 10,
  wardrobes: 14,
  sofa_2: 10,
  sofa_3: 14,
  sofa_corner: 20,
  suite_3pc: 24,
  armchairs: 5,
  dining_tables: 8,
  fridges: 7,
  washing_machines: 6,
  tvs: 2,
  bunk_beds: 10, cots: 3, mattresses: 3, chest_drawers: 6, dressing_tables: 5, bedside_tables: 2, mirrors: 1,
  sofa_bed: 15, coffee_tables: 3, tv_units: 4, bookcases: 6, cabinets: 7, lamps: 1, rugs: 1, piano: 15,
  dining_chairs: 1.5, american_fridge: 12, tumble_dryers: 6, dishwashers: 6, cookers: 6, microwaves: 1, small_appliances: 0.5, kitchen_cupboards: 7,
  hoovers: 1, ironing: 1, home_desks: 6, home_office_chairs: 3, computers_home: 1.5, heaters_fans: 1, speakers: 1, kids_toys: 2, pushchairs: 2, suitcases: 1.5, storage_tubs: 1.5,
  bikes: 4, gym_equipment: 8, treadmills: 10, garden_furniture: 6, bbq: 3, lawnmowers: 3, plants: 1,
  // Office
  desks: 8,
  office_chairs: 3,
  filing_cabinets: 6,
  computers: 1,
  printers: 3,
  bookshelves: 7,
  meeting_tables: 10,
  server_racks: 7,
  // Clearance — bin_bags = anchor (1 unit each)
  bin_bags: 1,
  c_loose: 8,
  cardboard: 0.6,
  c_polystyrene: 2,
  c_sofa_2: 10,
  c_sofa_3: 14,
  c_sofa_corner: 20,
  c_sofa_bed: 16,
  c_suite_3pc: 24,
  c_armchairs: 5,
  c_mattresses: 6,
  c_divans: 8,
  c_beds: 7,
  c_wardrobes: 14,
  c_drawers: 8,
  c_fridges: 7,
  c_fridge_american: 14,
  c_appliances: 7,
  c_cooker: 7,
  c_microwave: 1,
  c_tvs: 2,
  c_dining_table: 8,
  c_bookshelf: 7,
  // Wood — anchored so 100 broken-wood-furniture pieces ≈ full XLWB
  wood_light: 1,
  wood_heavy: 2,
  wood_pallets: 3,
  wood_furniture: 1,
  // Rubble & builder
  rubble_bags: 1,
  rubble_loose: 14,
  c_plasterboard: 1.5,
  c_tiles: 1,
  c_paint: 0.5,
  // Metal
  metal_light: 2,
  metal_heavy: 5,
  metal_appliances: 7,
  metal_bikes: 4,
  // Garden
  c_garden: 1.5,
  soil_bags: 1.2,
  c_shed: 18,
  c_garden_furniture: 6,
  // Flooring
  c_carpet: 3,
  c_laminate: 2.5,
  // Office
  c_office_desks: 8,
  c_office_chairs: 3,
  c_filing: 6,
  c_retail: 10,
  // Specialist
  c_piano: 24,
  c_pool_table: 28,
  c_hot_tub: 32,
  c_safe: 14,
  c_flytip: 16,
  c_general: 8,
};

export const computeVanFill = (form) => {
  let units = 0;
  const items = form.items || {};
  Object.entries(items).forEach(([id, qty]) => {
    units += (VOLUME_UNITS[id] || 0) * (Number(qty) || 0);
  });
  ['boxes_small', 'boxes_medium', 'boxes_large'].forEach((k) => {
    units += (VOLUME_UNITS[k] || 0) * (Number(form[k]) || 0);
  });
  return units;
};

// Capacity thresholds anchored to 110 bin bags = full XLWB.
export const CAP_XLWB = 110;
export const CAP_LUTON = 160;
export const CAP_LUTON_PLUS = 240;
export const CAP_7_5T = 400;
